import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal, { SectionLabel } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { PROGRAMS, BRAND } from "../lib/site";

const FIELDS = [
  { name: "parentName", label: "Parent Name", type: "text", required: true, col: 1 },
  { name: "childName", label: "Child Name", type: "text", required: true, col: 1 },
  { name: "age", label: "Age", type: "number", required: true, col: 1 },
  { name: "grade", label: "Grade", type: "text", required: true, col: 1 },
  { name: "phone", label: "Phone", type: "tel", required: true, col: 1 },
  { name: "email", label: "Email", type: "email", required: true, col: 1 },
];

export default function Join() {
  const [data, setData] = useState({
    parentName: "",
    childName: "",
    age: "",
    grade: "",
    phone: "",
    email: "",
    program: PROGRAMS[0].title,
    message: "",
  });
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    // static deploy: open mail client with prefilled enquiry
    const subject = `BTSA Enquiry — ${data.program} — ${data.childName || "Child"}`;
    const body =
      `Parent Name: ${data.parentName}\n` +
      `Child Name: ${data.childName}\n` +
      `Age: ${data.age}\n` +
      `Grade: ${data.grade}\n` +
      `Phone: ${data.phone}\n` +
      `Email: ${data.email}\n` +
      `Program Interest: ${data.program}\n\n` +
      `Message:\n${data.message}\n`;
    const url = `mailto:${BRAND.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    setTimeout(() => {
      setSending(false);
      navigate("/thank-you");
    }, 600);
  };

  return (
    <>
      <PageHero
        label="Join A Program"
        title={<>Begin your child's <em className="font-serif italic gold-text">BTSA journey.</em></>}
        subtitle="Share a few details and our admissions team will respond within one working day with the next step — a complimentary discovery session."
      />

      <section data-testid="join-section" className="py-24 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-8">
            <Reveal>
              <SectionLabel>Direct Channels</SectionLabel>
              <div className="mt-6 space-y-7 font-sans text-white/70">
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-white/40">Call</div>
                  <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="block mt-2 font-serif text-2xl text-white">
                    {BRAND.phone}
                  </a>
                </div>
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-white/40">Email</div>
                  <a href={`mailto:${BRAND.email}`} className="block mt-2 font-serif text-lg text-white break-all">
                    {BRAND.email}
                  </a>
                </div>
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-white/40">WhatsApp</div>
                  <a
                    href={`https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block mt-2 font-serif text-lg text-[#d4af37]"
                  >
                    Message Us →
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="hairline p-6">
                <div className="section-label">Response Window</div>
                <div className="mt-3 font-serif text-2xl text-white font-light">Within 24 hours</div>
                <p className="mt-2 font-sans text-sm text-white/55">
                  Monday – Saturday. Our team personally responds to every enquiry.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal>
              <form
                data-testid="join-form"
                onSubmit={submit}
                className="lux-card p-8 lg:p-10"
              >
                <h2 className="font-serif text-3xl text-white font-light">
                  Enquiry Form
                </h2>
                <p className="mt-2 font-sans text-sm text-white/55">
                  Required fields are marked. We respect your privacy entirely.
                </p>

                <div className="mt-9 grid sm:grid-cols-2 gap-5">
                  {FIELDS.map((f) => (
                    <div key={f.name}>
                      <label className="section-label">{f.label}{f.required && " *"}</label>
                      <input
                        data-testid={`join-input-${f.name}`}
                        type={f.type}
                        required={f.required}
                        value={data[f.name]}
                        onChange={(e) => set(f.name, e.target.value)}
                        className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37] transition-colors"
                      />
                    </div>
                  ))}

                  <div className="sm:col-span-2">
                    <label className="section-label">Program Interest</label>
                    <select
                      data-testid="join-input-program"
                      value={data.program}
                      onChange={(e) => set("program", e.target.value)}
                      className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white focus:outline-none focus:border-[#d4af37]"
                    >
                      {PROGRAMS.map((p) => (
                        <option key={p.slug} value={p.title}>
                          {p.title} — {p.ageGroup}
                        </option>
                      ))}
                      <option value="Not sure yet">Not sure yet — please advise</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="section-label">Message</label>
                    <textarea
                      data-testid="join-input-message"
                      rows={5}
                      value={data.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="Tell us about your child, any goals, or questions you have…"
                      className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37] resize-none"
                    />
                  </div>
                </div>

                <button
                  data-testid="join-submit"
                  type="submit"
                  disabled={sending}
                  className="mt-10 btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 inline-flex items-center gap-3 disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Enquiry"} <ArrowUpRight size={14} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
